import type {
  ChatUi,
  BusOfferDaySelection,
  BusOfferSeatsSelection,
  BusOfferSegmentSelection,
} from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from '../../shared/ui/action.js';
import { busTimeLabel } from '@voll-intelligence/intelligence-ui-kit';
import { formatWeekdayDayMonth, parseDateOnly } from '@voll-intelligence/dates';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { GenUiEntry } from '../../shared/ui/registry.js';
import { BusOffersList } from './ui/bus_offers.js';
import { TRAVELER_ID, type BusSeatTraveler } from './ui/bus_offer_seats.js';

type BusOffersUi = Extract<ChatUi, { kind: 'bus_offers' }>;

/** Quem escolhe poltrona: os viajantes da busca (#5472) ou, num bloco de um
 *  viajante só, quem conversa. */
function seatTravelersOf(ui: BusOffersUi): BusSeatTraveler[] | undefined {
  if (ui.travelers) return ui.travelers;
  return ui.traveler ? [{ id: TRAVELER_ID, name: ui.traveler.name }] : undefined;
}

/**
 * O card com a escolha já localizada.
 *
 * **Componente e não um lambda no `render` (#2327).** O texto do eco era um
 * template literal em pt-BR, e numa sessão em espanhol ele voltava em português —
 * o eco é a fala que a PESSOA aparenta ter dito, e ela não pode sair no idioma
 * errado. Traduzir exige o locale, o locale mora no `GenUiConfig`, e `render` é
 * uma função de catálogo: hook ali não roda.
 *
 * Card vivo (`challengeId`): a viagem com as poltronas volta por `interaction`
 * (o challenge do card + a `BusOfferSeatsSelection`), sem o LLM ler a escolha.
 * Bloco antigo (sem challenge): o pick é o quick-reply de texto.
 */
function BusOffersEntry({
  ui,
  note,
  readOnly,
  dispatch,
}: Readonly<{
  ui: BusOffersUi;
  note?: string;
  readOnly?: boolean;
  dispatch: GenUiDispatch;
}>) {
  const { t, locale } = useGenUiI18n();
  const live = !readOnly && ui.challengeId !== undefined;
  const dayText = (day: string): string => {
    const date = parseDateOnly(day);
    return date ? formatWeekdayDayMonth(date, locale, new Date()) : day;
  };
  const travelers = seatTravelersOf(ui);
  // Na ordem de quem viaja: um mapa com ids numéricos lista as chaves em ordem
  // crescente, nunca na do Quem.
  const codesOf = (seats: Record<string, string>): string =>
    (travelers ?? []).flatMap(({ id }) => (seats[id] ? [seats[id]] : [])).join(', ');
  const seatsText = (segments: BusOfferSegmentSelection[]): string =>
    segments
      .map((segment) => {
        if (segment.noSeat) return t('busOffers.pickNoSeat');
        const code = 'seats' in segment ? codesOf(segment.seats) : segment.seatCode;
        return t('busOffers.pickSeat', { code });
      })
      .join(' · ');
  return (
    <BusOffersList
      offers={ui.offers}
      {...(ui.allOffers ? { allOffers: ui.allOffers } : {})}
      {...(ui.leg ? { leg: ui.leg } : {})}
      {...(ui.route ? { route: ui.route } : {})}
      {...(ui.roundTrip ? { roundTrip: ui.roundTrip } : {})}
      {...(ui.outboundPick ? { outboundPick: ui.outboundPick } : {})}
      {...(travelers ? { travelers } : {})}
      {...(ui.autoExpand ? { autoExpand: ui.autoExpand } : {})}
      {...(note ? { note } : {})}
      {...(ui.day ? { day: ui.day } : {})}
      {...(ui.dayBounds ? { dayBounds: ui.dayBounds } : {})}
      {...(live
        ? {
            onPickDay: (day: string) => {
              const selection: BusOfferDaySelection = { day };
              dispatch({
                type: 'interaction',
                interactionId: ui.challengeId ?? '',
                text: t('busOffers.pickDayText', { day: dayText(day) }),
                data: selection,
              });
            },
            onConfirmSeats: (offer, segments) => {
              const selection: BusOfferSeatsSelection = { offerId: offer.offerId, segments };
              dispatch({
                type: 'interaction',
                interactionId: ui.challengeId ?? '',
                // A hora, não a posição: o card mostra a ordem do agente, que
                // não é a da lista inteira.
                text: t('busOffers.pickSeatsText', {
                  time: busTimeLabel(offer.departure.dateTime),
                  carrier: offer.carrierName,
                  seats: seatsText(segments),
                }),
                data: selection,
              });
            },
          }
        : {})}
      {...(!readOnly && !live
        ? {
            onPickOffer: (offer, position) => {
              dispatch({
                type: 'reply',
                text: t('busOffers.pickText', {
                  position,
                  carrier: offer.carrierName,
                  from: offer.departure.stopName,
                  to: offer.arrival.stopName,
                }),
              });
            },
          }
        : {})}
    />
  );
}

/** Entrada de catálogo do `bus_offers` (slot chat). O shell é só transporte:
 *  não conhece o offerId opaco, as poltronas nem o texto. */
export const busOffersEntry: GenUiEntry<BusOffersUi> = {
  kind: 'bus_offers',
  slot: 'chat',
  policy: { productOf: () => 'bus' },
  render: ({ ui, note, readOnly, dispatch }) => (
    <BusOffersEntry
      ui={ui}
      {...(note ? { note } : {})}
      {...(readOnly ? { readOnly } : {})}
      dispatch={dispatch}
    />
  ),
};
